import { Link } from "react-router-dom";
import {
  ArrowRight,
  Brain,
  ChatCircleDots,
  Compass,
  FileText,
  Lightbulb,
  Target,
} from "@phosphor-icons/react";
import WhistleIcon from "@/components/WhistleIcon";
import { trackPublicEvent } from "@/lib/publicAnalytics";
import SeoPageShell from "@/components/SeoPageShell";

const intelligenceLayers = [
  {
    icon: ChatCircleDots,
    label: "Dissection",
    title: "Start with evidence, not an unsupported conclusion.",
    copy: "Surface the specific customer and rep moments that changed the conversation, then connect each finding back to what was actually said.",
  },
  {
    icon: WhistleIcon,
    label: "Coaching",
    title: "Turn interpretation into a lesson you can reuse.",
    copy: "Show what could have been said differently, why another approach might have changed the conversation, and which moment created the coaching opportunity.",
  },
  {
    icon: Compass,
    label: "Relationship",
    title: "Carry the customer story across calls.",
    copy: "Preserve objections, commitments, stakeholder dynamics, risk, and buying intent so the next conversation starts with the real relationship state.",
  },
  {
    icon: Target,
    label: "Prepare Next Call",
    title: "Make the previous conversation useful before the next one.",
    copy: "Turn accumulated context into what to validate, what to ask, what to avoid, and which outcome should move the relationship forward.",
  },
];

function trackCta(section, ctaId) {
  trackPublicEvent("cta_click", {
    source: "conversation_intelligence_vs_call_summarization",
    section,
    cta_id: ctaId,
  });
}

export default function ConversationIntelligenceVsCallSummarizationPage() {
  return (
    <SeoPageShell source="conversation_intelligence_vs_call_summarization">
      <main>
        <section className="seo-marketing-hero product-feature-hero relative overflow-hidden px-5 pb-20 pt-16 sm:px-8 sm:pb-24 sm:pt-24">
          <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
            <div className="absolute left-[-12%] top-[-14rem] h-[36rem] w-[36rem] rounded-full bg-indigo-700/20 blur-[135px]" />
            <div className="absolute right-[-8%] top-[2rem] h-[28rem] w-[28rem] rounded-full bg-violet-700/10 blur-[135px]" />
          </div>
      
          <div className="relative mx-auto max-w-7xl">
      
            <h1 className="seo-marketing-hero__title product-feature-hero__title landing-hero-title max-w-5xl">
              Conversation Intelligence vs Call Summarization:
              <span className="sales-call-review-gradient product-feature-hero__title-accent landing-hero-spectrum block">What’s Actually Different?</span>
            </h1>
      
            <p className="seo-marketing-hero__subcopy product-feature-hero-subcopy landing-hero-subcopy marketing-lead-copy">
              A call summary compresses what happened. Useful conversation intelligence should help you understand what the conversation means: which evidence matters, what changed, what could have been handled differently, and what should happen next. The difference is not more notes. It is more usable reasoning.
            </p>
      
            <div className="seo-marketing-hero__actions product-feature-hero__actions">
              <Link
                to="/login?mode=register"
                onClick={() => trackCta("hero", "sign_up_free")}
                className="product-feature-hero-primary landing-signup-primary-cta inline-flex items-center justify-center rounded-xl marketing-primary-action border px-5 py-3.5 font-medium transition"
              >
                Sign up free
              </Link>
              <Link
                to="/demo"
                onClick={() => trackCta("hero", "see_demo")}
                className="product-feature-hero-secondary landing-hero-secondary-cta inline-flex items-center justify-center gap-2 rounded-xl marketing-secondary-action border px-5 py-3.5 font-medium marketing-text-secondary-strong transition"
              >
                See Demo
                <ArrowRight size={17} weight="bold" />
              </Link>
            </div>
          </div>
        </section>
      
        <section id="difference" className="scroll-mt-24 border-y border-white/[0.06] bg-white/[0.018] px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <span className="sales-call-review-kicker">The category boundary</span>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
                Summarization answers “what happened?” Intelligence should answer “so what?”
              </h2>
              <p className="marketing-lead-copy mt-6">
                Summaries are useful. They reduce a long conversation into something faster to scan. The problem starts when that compression is treated as the analysis itself.
              </p>
            </div>
      
            <div className="conversation-intelligence-compare mt-12">
              <article className="conversation-intelligence-compare__column">
                <div className="conversation-intelligence-compare__eyebrow">
                  <FileText size={19} weight="duotone" />
                  CALL SUMMARIZATION
                </div>
                <h3>Compress the conversation.</h3>
                <div className="conversation-intelligence-compare__questions">
                  <span>What topics came up?</span>
                  <span>What decisions were made?</span>
                  <span>What action items were mentioned?</span>
                  <span>What can I read instead of the transcript?</span>
                </div>
                <p>
                  This is valuable retrieval and documentation. It tells you what the call contained without requiring you to replay it.
                </p>
              </article>
      
              <div className="conversation-intelligence-compare__divider" aria-hidden="true">
                <ArrowRight size={24} weight="bold" />
              </div>
      
              <article className="conversation-intelligence-compare__column is-intelligence">
                <div className="conversation-intelligence-compare__eyebrow">
                  <Brain size={19} weight="duotone" />
                  CONVERSATION INTELLIGENCE
                </div>
                <h3>Interpret the conversation.</h3>
                <div className="conversation-intelligence-compare__questions">
                  <span>Which moments changed the commercial picture?</span>
                  <span>What do those moments imply about risk or intent?</span>
                  <span>What could the rep have handled differently?</span>
                  <span>What should the next conversation accomplish?</span>
                </div>
                <p>
                  This is the reasoning layer: evidence becomes interpretation, coaching, relationship context, and a concrete next move.
                </p>
              </article>
            </div>
          </div>
        </section>
      
        <section className="px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-start lg:gap-16">
            <div className="lg:sticky lg:top-24">
              <span className="sales-call-review-kicker">Same call, different output</span>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
                The distinction becomes obvious when both systems see the same sentence.
              </h2>
              <p className="marketing-lead-copy mt-6">
                A summary can preserve the statement correctly. Conversation intelligence becomes useful when it connects that statement to the state of the deal and the behavior of the rep.
              </p>
            </div>
      
            <div className="conversation-intelligence-example">
              <div className="conversation-intelligence-example__quote">
                <span>CLIENT</span>
                <blockquote>
                  “I’m comfortable with the price. I’m just not sure our operations lead will want to change the process this quarter.”
                </blockquote>
              </div>
      
              <div className="conversation-intelligence-example__output is-summary">
                <div className="conversation-intelligence-example__label">
                  <FileText size={18} weight="duotone" />
                  CALL SUMMARY
                </div>
                <p>
                  The customer is comfortable with pricing but has concerns about getting the operations lead aligned on changing the current process this quarter.
                </p>
              </div>
      
              <div className="conversation-intelligence-example__output is-intelligence">
                <div className="conversation-intelligence-example__label">
                  <Lightbulb size={18} weight="duotone" />
                  INTELLIGENCE LAYER
                </div>
                <div className="conversation-intelligence-example__finding">
                  <strong>Interpretation</strong>
                  <p>Price is not the active objection. The deal is exposed to stakeholder alignment and perceived operational disruption.</p>
                </div>
                <div className="conversation-intelligence-example__finding">
                  <strong className="seo-coaching-heading"><WhistleIcon size={15} weight="duotone" />Coaching</strong>
                  <p>Stop reinforcing the pricing case. Bring the missing stakeholder and implementation concern into the conversation directly.</p>
                </div>
                <div className="conversation-intelligence-example__finding">
                  <strong>Next move</strong>
                  <p>Get the operations lead into the next call and validate what would make a process change feel safe enough to support this quarter.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      
        <section className="border-y border-white/[0.06] bg-white/[0.018] px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <span className="sales-call-review-kicker">What intelligence should carry forward</span>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
                Understanding one call is useful. Remembering what changed across calls is stronger.
              </h2>
              <p className="marketing-lead-copy mt-6">
                This is where Callsect’s definition becomes intentionally practical. The output should not end when the post-call page closes. It should improve how you understand the relationship and how you enter the next conversation.
              </p>
            </div>
      
            <div className="conversation-intelligence-layers mt-12">
              {intelligenceLayers.map(({ icon: Icon, label, title, copy }, index) => (
                <article key={label} className="conversation-intelligence-layer">
                  <div className="conversation-intelligence-layer__topline">
                    <span>0{index + 1}</span>
                    <Icon size={21} weight="duotone" />
                  </div>
                  <span className="conversation-intelligence-layer__label">{label}</span>
                  <h3>{title}</h3>
                  <p>{copy}</p>
                </article>
              ))}
            </div>
      
            <p className="mt-7 text-sm leading-6 text-slate-500">
              Want to turn that relationship history into a concrete pre-call plan?{" "}
              <Link
                to="/prepare-for-next-customer-call"
                onClick={() => trackCta("internal_link", "prepare_for_next_customer_call")}
                className="font-medium text-indigo-300 transition hover:text-indigo-200"
              >
                See how to prepare for the next customer call from the previous conversation
              </Link>
              .
            </p>
          </div>
        </section>
      
        <section className="px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
            <div>
              <span className="sales-call-review-kicker">A useful test</span>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
                Ask what the output changes about the next action.
              </h2>
              <p className="marketing-lead-copy mt-6">
                The label “conversation intelligence” can cover a wide range of capabilities. A practical way to evaluate the result is simpler: after reading it, do you merely know the call better, or do you know how to act differently because of it?
              </p>
            </div>
      
            <div className="conversation-intelligence-test">
              <div>
                <span>SUMMARY-LEVEL VALUE</span>
                <strong>I know what was discussed.</strong>
                <p>I can review the call faster, capture decisions, and avoid replaying the recording.</p>
              </div>
              <ArrowRight size={22} weight="bold" aria-hidden="true" />
              <div className="is-intelligence">
                <span>INTELLIGENCE-LEVEL VALUE</span>
                <strong>I know what changed and what I should do with it.</strong>
                <p>I can trace the evidence, understand the commercial meaning, improve my approach, and prepare the next move from the relationship history.</p>
              </div>
            </div>
          </div>
        </section>
      
        <section className="px-5 pb-10 sm:px-8 sm:pb-14">
          <div className="conversation-intelligence-related mx-auto max-w-5xl">
            <div>
              <span className="sales-call-review-kicker">Go one level deeper</span>
              <h2>What should sales call analysis software actually do?</h2>
              <p>
                If you are evaluating tools rather than the category itself, use the sales call analysis framework: evidence → interpretation → coaching → next move → relationship context.
              </p>
            </div>
            <Link
              to="/sales-call-analysis-software"
              onClick={() => trackCta("internal_link", "sales_call_analysis_software")}
              className="marketing-consistency-secondary-link product-feature-final__secondary"
            >
              Sales call analysis software
              <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </section>
      
        <section className="px-5 pb-20 pt-6 sm:px-8 sm:pb-28 sm:pt-10">
          <div className="product-feature-final sales-call-review-cta mx-auto max-w-5xl">
            <span className="sales-call-review-kicker">See the difference in the product</span>
            <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
              Follow a sales conversation beyond the summary.
            </h2>
            <p className="marketing-lead-copy mx-auto mt-6 max-w-2xl">
              Explore the real Callsect interface with synthetic sales-call data and follow the workflow from Dissection and Coaching into Relationship and Prepare Next Call.
            </p>
            <div>
              <Link
                to="/login?mode=register"
                onClick={() => trackCta("final", "sign_up_free")}
                className="product-feature-final__primary"
              >
                Sign up free
              </Link>
              <Link
                to="/demo"
                onClick={() => trackCta("final", "see_demo")}
                className="product-feature-final__secondary"
              >
                See Demo
                <ArrowRight size={17} weight="bold" />
              </Link>
            </div>
          </div>
        </section>
      </main>
    </SeoPageShell>
  );
}
